import React from 'react';
import { ArrowLeftIcon, DownloadIcon } from 'lucide-react';
import type { CognitiveState, PlayerCri } from '../../types/neuro';
import { neuroTeams } from '../../data/neuroPlayers';
import { criLabel, criTone, formatMeta, interpretation, isLimitedSample, toneClasses, type StatusTone } from '../../utils/neuro';
import { InfoTooltip } from './InfoTooltip';
import { LimitedBadge, NeuroBar, PlayerAvatar } from './NeuroPrimitives';
interface PlayerCriDetailProps {
  player: PlayerCri;
  onBack: () => void;
  onDownload: () => void;
}
const STATE_STYLES: Record<CognitiveState, {
  bar: string;
  tone: StatusTone | 'off';
}> = {
  Ready: {
    bar: 'bg-[#16A34A]',
    tone: 'ready'
  },
  Alert: {
    bar: 'bg-[#F59E0B]',
    tone: 'moderate'
  },
  Declining: {
    bar: 'bg-[#DC2626]',
    tone: 'low'
  },
  Off: {
    bar: 'bg-[#7F1D1D]',
    tone: 'off'
  }
};
const SectionTitle = ({
  children
}: {
  children: React.ReactNode;
}) => <h3 className="text-sm font-semibold text-[#111827]" data-id="element-620" data-mp="3ipwy683ostum">{children}</h3>;
const Panel = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <section className={`rounded-xl border border-[#E5E7EB] bg-white p-5 ${className}`} data-id="element-621" data-mp="n9qs4gfhpek8">
    {children}
  </section>;
export const PlayerCriDetail = ({
  player,
  onBack,
  onDownload
}: PlayerCriDetailProps) => {
  const team = neuroTeams[player.team];
  const tone = criTone(player.cri_match);
  const limited = isLimitedSample(player);
  const indices = [{
    label: 'Focus',
    value: player.fi_match,
    note: null,
    help: 'How consistently the player made sound, on-task decisions during their minutes.'
  }, {
    label: 'Impulsivity',
    value: player.ii_match,
    note: 'lower is better',
    help: 'How often the player acted too early or forced plays. A lower number means more controlled decisions.'
  }, {
    label: 'Reaction',
    value: player.rei_match,
    note: null,
    help: 'How quickly the player bounced back with a positive action after making a mistake.'
  }];
  const quarters = [{
    label: 'Q1',
    value: player.fi_q1
  }, {
    label: 'Q2',
    value: player.fi_q2
  }, {
    label: 'Q3',
    value: player.fi_q3
  }, {
    label: 'Q4',
    value: player.fi_q4
  }];
  const states: Array<{
    state: CognitiveState;
    pct: number;
  }> = [{
    state: 'Ready',
    pct: player.time_pct_ready
  }, {
    state: 'Alert',
    pct: player.time_pct_alert
  }, {
    state: 'Declining',
    pct: player.time_pct_declining
  }, {
    state: 'Off',
    pct: player.time_pct_off
  }];
  const finalStyle = STATE_STYLES[player.rei_state_final];
  const finalToneClass = finalStyle.tone === 'off' ? 'text-[#7F1D1D]' : toneClasses[finalStyle.tone].text;
  const details = [{
    label: 'Off-state loops reached',
    value: String(player.loops_reached),
    valueClass: 'text-[#111827]',
    help: 'How many times the player got stuck repeating unproductive actions after an error.'
  }, {
    label: 'Final state',
    value: player.rei_state_final,
    valueClass: finalToneClass,
    help: 'The cognitive state the player was in at the end of their last stint.'
  }, {
    label: 'Actions to first recovery',
    value: player.error_to_first_positive.toFixed(1),
    valueClass: 'text-[#111827]',
    help: 'On average, how many actions it took after a mistake before the next positive play.'
  }, {
    label: 'Sample reliability',
    value: player.confidence_flag,
    valueClass: player.confidence_flag === 'Limited' ? toneClasses.moderate.text : toneClasses.ready.text,
    help: 'Whether there were enough in-game events for the indices to be reliable. Limited means treat values as indicative.'
  }];
  return <div data-id="element-622" data-mp="66ov4adgz4k6">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 mb-5" data-id="element-623" data-mp="3ocbnzsfroz6j">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2563EB] hover:text-[#1D4ED8] rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-624" data-mp="3oe4wzedurvhp">
          <ArrowLeftIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-625" data-mp="vid2a88cq2sb" />
          All players
        </button>
        <button type="button" onClick={onDownload} className="inline-flex items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white px-3 py-2 text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 whitespace-nowrap" data-id="element-626" data-mp="lrrwo0u9fxzw">
          <DownloadIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-627" data-mp="7ssbj35yi18h" />
          Download player report
        </button>
      </div>

      {/* Player header */}
      <div className="flex items-center gap-4 pb-5 border-b border-[#E5E7EB]" data-id="element-628" data-mp="2269yop2g8w2z">
        <PlayerAvatar player={player} size={56} data-id="element-629" data-mp="1x76u15qdsrvj" />
        <div className="min-w-0" data-id="element-630" data-mp="1x7giupzowszn">
          <div className="flex flex-wrap items-center gap-2" data-id="element-631" data-mp="24qg2mwr2cmjj">
            <h3 className="text-xl font-bold text-[#111827]" data-id="element-632" data-mp="24pwug7r7tcip">{player.name}</h3>
            {limited && <LimitedBadge data-id="element-633" data-mp="29o83ig9orizv" />}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[#6B7280]" data-id="element-634" data-mp="2eg9cntbk4tlo">
            <span className={`inline-flex items-center gap-1.5 font-medium ${team.textClass}`} data-id="element-635" data-mp="2jfcihfzd33up">
              <span className={`w-2 h-2 rounded-full ${team.dotClass}`} aria-hidden="true" data-id="element-636" data-mp="1lrk9loqxye7a" />
              {team.name}
            </span>
            <span aria-hidden="true" data-id="element-637" data-mp="1qrq55g0c0kun">·</span>
            <span data-id="element-638" data-mp="1qsrjbtcn9ngl">{player.position}</span>
            <span aria-hidden="true" data-id="element-639" data-mp="1vp8e36ecn02n">·</span>
            <span data-id="element-640" data-mp="1vogl83c4zgg2">{formatMeta(player)}</span>
          </div>
        </div>
      </div>

      {/* CRI hero + indices */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-4" data-id="element-641" data-mp="1zp0hz5sssi1o">
        <Panel className={`lg:col-span-4 ${toneClasses[tone].bg} border-transparent`} data-id="element-642" data-mp="374djii5v7cpy">
          <div className="text-xs font-medium text-[#374151]" data-id="element-643" data-mp="3b578lev1mxkf">
            Cognitive Readiness Index
          </div>
          <div className="mt-2 flex items-end gap-3" data-id="element-644" data-mp="1bzxfbmgbm26r">
            <span className={`text-[56px] leading-none font-semibold tabular-nums ${toneClasses[tone].text}`} data-id="element-645" data-mp="1737174o2tgnz">
              {player.cri_match}
            </span>
            <span className={`mb-1.5 inline-flex items-center gap-1.5 text-sm font-medium ${toneClasses[tone].text}`} data-id="element-646" data-mp="1l02nhytc94lz">
              <span className={`w-2 h-2 rounded-full ${toneClasses[tone].dot}`} aria-hidden="true" data-id="element-647" data-mp="sdhvqx8yaqhw" />
              {criLabel[tone]}
            </span>
          </div>
          <p className="mt-3 text-sm text-[#111827]" data-id="element-648" data-mp="oe0mrtubfb7z">{interpretation(player)}</p>
        </Panel>

        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-649" data-mp="124vqystvd458">
          {indices.map(index => <Panel key={index.label} className="flex flex-col" data-id="element-650" data-mp="xb181rm38t77">
              <div className="flex items-center justify-between gap-2" data-id="element-651" data-mp="9lp353xsn7ap">
                <span className="text-sm font-medium text-[#374151]" data-id="element-652" data-mp="4oymofi2u9or">{index.label}</span>
                <InfoTooltip label={index.label} text={index.help} align="right" data-id="element-653" data-mp="2gb0h0l4iilgw" />
              </div>
              <div className="text-xs text-[#6B7280] h-4" data-id="element-654" data-mp="2l2sdfcacnley">{index.note}</div>
              <div className="mt-auto pt-3 text-[32px] leading-none font-semibold text-[#111827] tabular-nums" data-id="element-655" data-mp="27auajj78elr8">
                {index.value}
              </div>
              <div className="mt-3" data-id="element-656" data-mp="2cagjhgg11fu8">
                <NeuroBar value={index.value} data-id="element-657" data-mp="2zzjb9h4dkp2g" />
              </div>
            </Panel>)}
        </div>
      </div>

      {/* Focus by quarter + time in state */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-658" data-mp="340blodozh00b">
        <Panel data-id="element-659" data-mp="2q0ad0c2h8oaa">
          <SectionTitle data-id="element-660" data-mp="2q0ts7l0qsykm">Focus by quarter</SectionTitle>
          <div className="mt-4 grid grid-cols-4 gap-3" data-id="element-661" data-mp="2uzx0dec0rkwt">
            {quarters.map(quarter => <div key={quarter.label} className="flex flex-col items-center rounded-lg bg-[#F9FAFB] px-2 pt-3 pb-2" data-id="element-662" data-mp="1elnc0k7s1w5z">
                <div className="h-20 w-3 rounded-full bg-[#E5E7EB] flex items-end overflow-hidden" aria-hidden="true" data-id="element-663" data-mp="1jj6rtok4ih01">
                  <div className="w-full rounded-full bg-[#64748B]" style={{
                height: `${quarter.value}%`
              }} data-id="element-664" data-mp="269ri0omo8901" />
                </div>
                <div className="mt-2 text-sm font-semibold text-[#111827] tabular-nums" data-id="element-665" data-mp="21chq92bt7l91">
                  {quarter.value.toFixed(1)}
                </div>
                <div className="text-xs text-[#6B7280]" data-id="element-666" data-mp="2ty0xl8tak0iq">{quarter.label}</div>
              </div>)}
          </div>
        </Panel>

        <Panel data-id="element-667" data-mp="2p2lmupqkykqj">
          <SectionTitle data-id="element-668" data-mp="2k9995anfvyck">Time in each cognitive state</SectionTitle>
          <div className="mt-5 flex h-4 w-full overflow-hidden rounded-full" role="img" aria-label={states.map(s => `${s.state} ${s.pct}%`).join(', ')} data-id="element-669" data-mp="2k7fy7j5smryr">
            {states.map(s => <div key={s.state} className={`${STATE_STYLES[s.state].bar} h-full`} style={{
            width: `${s.pct}%`
          }} data-id="element-670" data-mp="37ylg03vanubp" />)}
          </div>
          <ul className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-671" data-mp="3k8ktl70hpptv">
            {states.map(s => <li key={s.state} className="flex items-start gap-2" data-id="element-672" data-mp="3p861w81jvaj9">
                <span className={`mt-1 w-2.5 h-2.5 rounded-sm ${STATE_STYLES[s.state].bar}`} aria-hidden="true" data-id="element-673" data-mp="3u7inkpgoqbk9" />
                <div data-id="element-674" data-mp="31r2i4hrlp4b">
                  <div className="text-xs text-[#6B7280]" data-id="element-675" data-mp="306aommu4ka5">{s.state}</div>
                  <div className="text-sm font-semibold text-[#111827] tabular-nums" data-id="element-676" data-mp="72ayv5uikope">{s.pct}%</div>
                </div>
              </li>)}
          </ul>
        </Panel>
      </div>

      {/* Report + details */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4" data-id="element-677" data-mp="grks4p13gdf3">
        <Panel className="lg:col-span-7" data-id="element-678" data-mp="lrz24cm99xfr">
          <SectionTitle data-id="element-679" data-mp="lta4q93ysz5a">Match report</SectionTitle>
          {player.report_text ? <p className="mt-3 text-sm leading-relaxed text-[#374151]" data-id="element-680" data-mp="qrv302znp8r3">
              {player.report_text}
            </p> : <p className="mt-3 text-sm text-[#6B7280]" data-id="element-681" data-mp="3fjgdxxmgp5fc">
              Report not available yet for this player.
            </p>}
        </Panel>

        <section className="lg:col-span-5 flex flex-col" data-id="element-682" data-mp="3ol6cdnerh4mm">
          <div className="mb-3" data-id="element-683" data-mp="3tcxvxdzi2a39">
            <SectionTitle data-id="element-684" data-mp="3td7pfbjr6zdp">Cognitive details</SectionTitle>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1" data-id="element-685" data-mp="26wqbkyb08p0">
            {details.map(detail => <div key={detail.label} className="rounded-xl border border-[#E5E7EB] bg-white p-4 flex flex-col" data-id="element-686" data-mp="757xtp14m742">
                <div className="flex items-start justify-between gap-2" data-id="element-687" data-mp="b5i5r78hb7lk">
                  <span className="text-xs text-[#6B7280]" data-id="element-688" data-mp="g5w6033u9d91">{detail.label}</span>
                  <InfoTooltip label={detail.label} text={detail.help} align="right" data-id="element-689" data-mp="2czr7fhak5y4h" />
                </div>
                <div className={`mt-auto pt-2 text-lg font-semibold tabular-nums ${detail.valueClass}`} data-id="element-690" data-mp="2hykb6i01marg">
                  {detail.value}
                </div>
              </div>)}
          </div>
        </section>
      </div>
    </div>;
};
export default PlayerCriDetail;